import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';

const FoodIcon = ({ emoji, x, y }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            className="absolute text-6xl cursor-pointer"
            style={{ left: x, top: y }}
            animate={isHovered ? {
                x: Math.random() * 20 - 10,
                y: Math.random() * 20 - 10,
                rotate: Math.random() * 360,
                transition: { duration: 0.3 }
            } : {}}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
        >
            {emoji}
        </motion.div>
    );
};

const FrontPage = () => {
    const [isSignIn, setIsSignIn] = useState(true);
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const action = params.get('action');
        if (action === 'signin') setIsSignIn(true);
        else if (action === 'create') setIsSignIn(false);
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            if (isSignIn) {
                const response = await axiosInstance.post('/api/auth/login', {
                    username,
                    password
                });

                if (response?.data?.token) {
                    localStorage.setItem('token', response.data.token);
                    setSuccess('Logged in successfully!');
                    setTimeout(() => navigate('/dashboard'), 500);
                }
            } else {
                if (password !== confirmPassword) {
                    setError('Passwords do not match');
                    setIsLoading(false);
                    return;
                }

                const response = await axiosInstance.post('/api/auth/register', {
                    username,
                    email,
                    password
                });

                if (response?.data?.token) {
                    localStorage.setItem('token', response.data.token);
                    setSuccess('Registered and logged in successfully!');
                    setTimeout(() => navigate('/dashboard'), 500);
                }
            }
        } catch (error) {
            let errorMessage = 'Authentication failed. Please try again.';
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        errorMessage = 'Service not found. Please check your connection.';
                        break;
                    case 401:
                        errorMessage = 'Invalid username or password';
                        break;
                    case 409:
                        errorMessage = 'Username or email already exists';
                        break;
                    case 400:
                        errorMessage = error.response.data?.message || 'Invalid input provided';
                        break;
                    case 500:
                        errorMessage = 'Server error. Please try again later';
                        break;
                    default:
                        errorMessage = error.response.data?.message || 'Authentication failed';
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to server. Please check your internet connection';
            }
            console.error('Auth Error:', error.response || error);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    
    const toggleMode = () => {
        setIsSignIn(!isSignIn);
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setError('');
        setSuccess('');
    };

    const emojis = [
        { emoji: '🍔', x: '5%', y: '10%' },
        { emoji: '🍕', x: '90%', y: '15%' },
        { emoji: '🌮', x: '80%', y: '85%' },
        { emoji: '🥗', x: '10%', y: '80%' },
    ];

    return (
        <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-1 relative overflow-hidden">
            {emojis.map((item, index) => (
                <FoodIcon key={index} emoji={item.emoji} x={item.x} y={item.y} />
            ))}

            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-8 z-10 mt-16"
            >
                <h1 className="text-6xl font-extrabold text-orange-500 mb-2">WrappedXpress</h1>
                <p className="text-xl text-teal-700 mb-2">Downingtown STEM Academy's Official Lunch Ordering App</p>
                <Link to="/ios-app" className="text-sm text-green-500 font-medium">
                    Now available on iOS @ WrappedXpress
                </Link>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md z-20 relative"
            >
                <h2 className="text-2xl font-bold text-teal-700 mb-6">
                    {isSignIn ? 'Sign in to Order Today' : 'Create Your Account'}
                </h2>
                {error && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-red-500 mb-4"
                    >
                        {error}
                    </motion.p>
                )}
                {success && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-500 mb-4"
                    >
                        {success}
                    </motion.p>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <input
                            type="text"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-3 border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                            required
                            disabled={isLoading}
                        />
                    </div>
                    {!isSignIn && (
                        <div>
                            <input
                                type="email"
                                placeholder="Enter your school email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    )}
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder={isSignIn ? "Enter your password" : "Create a password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                            required
                            disabled={isLoading}
                        />
                        <motion.button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-3 text-teal-500"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            disabled={isLoading}
                        >
                            {showPassword ? '👁️' : '👁️‍🗨️'}
                        </motion.button>
                    </div>
                    {!isSignIn && (
                        <div>
                            <input
                                type="password"
                                placeholder="Confirm password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    )}
                    <motion.button
                        type="submit"
                        className={`w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg transition duration-300 ease-in-out flex items-center justify-center group ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        whileHover={!isLoading ? { scale: 1.05 } : {}}
                        whileTap={!isLoading ? { scale: 0.95 } : {}}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <>
                                {isSignIn ? 'Sign In' : 'Create Account'}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-200"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </>
                        )}
                    </motion.button>
                </form>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-8 text-center z-10 mb-20"
            >
                <p className="text-teal-700 mb-2">
                    {isSignIn ? "New to WrappedXpress?" : "Already have an account?"}
                </p>
                <motion.button
                    onClick={toggleMode}
                    className="text-orange-500 hover:text-orange-600 font-semibold transition duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isLoading}
                >
                    {isSignIn ? "Create an account" : "Sign in"}
                </motion.button>
            </motion.div>
        </div>
    );
};

export default FrontPage;